<template>
  <v-container fluid>
    <h2>{{ $tt('feed.trajectories') }}</h2>
    <p class="ml-2 mb-0">{{ dateFilterRange }}</p>
    <task-filter-component ref="taskFilterComponent" @on-set-filter="resetPageIndex()" />
    <v-layout justify-start align-center class="ml-2">
      <v-checkbox v-model="isBulkDelete" hide-details :label="$tt('customBulkDeleteCheckboxText')"></v-checkbox>
      <v-btn v-if="isBulkDelete" @click="isShowBuldDeleteDialog = true" color="error" class="mt-3 ml-4">
        <v-icon>mdi-trash-can-outline</v-icon>
      </v-btn>
    </v-layout>

    <v-row v-if="tasks.length > 0">
      <!-- table表示 -->
      <v-col cols="12" md="12">
        <div v-resize="onResize">
          <div v-if="!isMobile">
            <v-simple-table>
              <tbody>
                <tr
                  v-for="task in tasks"
                  :key="task.geoInfoKey"
                  :class="{ blur: hidden(task), 'clickable-row': !hidden(task) }"
                  @click="onRowClick(task)"
                >
                  <td class="pa-0" v-if="isBulkDelete" @click.stop>
                    <v-list-item style="width:20px">
                      <v-list-item-action>
                        <v-checkbox :input-value="isDeleteChecked(task)" @change="toggleDeleteCheck(task)"></v-checkbox>
                      </v-list-item-action>
                    </v-list-item>
                  </td>
                  <!-- 日付、作業面積 -->
                  <td>
                    <v-list-item two-line>
                      <v-list-item-content>
                        <v-list-item-subtitle>{{ task.startTime | aidToYearMonthDayHourMin }}</v-list-item-subtitle>
                        <v-list-item-title>{{ task | aidTaskRecrodAreaFormatted }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </td>
                  <!-- 作業タイプ -->
                  <td>
                    <operation-type-indicator-component :operationType="task.operationType" :displayBorder="true" />
                  </td>
                  <!-- アイコン、圃場情報 -->
                  <td>
                    <v-list style="background: none" two-line>
                      <v-list-item>
                        <user-icon-component
                          size="36"
                          :showNickname="true"
                          :iconUrl="getAccount(task).iconUrl"
                          :nickname="getAccount(task).nickname"
                          class="mr-2"
                        />
                        <v-list-item-content>
                          <v-list-item-title>
                            <strong v-if="task" class="d-inline-block text-truncate" style="max-width: 140px">
                              {{ getFieldNameFromFieldGeoInfoKeyFromTask(task) }}
                            </strong>
                            <strong v-else>-----</strong>
                          </v-list-item-title>
                          <v-list-item-subtitle>{{ task.name }}</v-list-item-subtitle>
                          <div v-for="p in plantings(task)">
                            <planting-indicator-component :planting="p" small :display-year="false" />
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </td>

                  <!-- 作業情報 -->
                  <td>
                    <v-list style="background: none" two-line>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>{{ task | aidTaskRecordEfficiencyDescription }}</v-list-item-subtitle>
                          <v-list-item-title>{{ task | aidTaskRecordDistanceDescription }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </td>
                  <td v-if="!hidden(task)" @click.stop>
                    <v-btn color="error" @click="openDeleteDialog(task)" :disabled="!isMyTask(task)">
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- card表示 -->
          <div v-else>
            <v-hover v-slot:default="{ hover }" v-for="task in tasks" :key="task.geoInfoKey">
              <v-card
                :elevation="hover ? 12 : 2"
                color="mb-4"
                :class="{ blur: hidden(task), 'clickable-row': !hidden(task) }"
                @click="onRowClick(task)"
              >
                <v-layout justify-start align-center>
                  <v-checkbox
                    v-if="isBulkDelete"
                    class="ml-2"
                    :input-value="isDeleteChecked(task)"
                    @click.stop
                    @change="toggleDeleteCheck(task)"
                  ></v-checkbox>
                  <v-list width="100%">
                    <!-- 日付、作業面積 -->
                    <v-list-item two-line>
                      <v-list-item-content>
                        <v-list-item-subtitle>{{ task.startTime | aidToYearMonthDayHourMin }}</v-list-item-subtitle>
                        <v-list-item-title>{{ task | aidTaskRecrodAreaFormatted }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <!-- 作業タイプ -->
                    <v-list-item>
                      <v-list-item-content>
                        <operation-type-indicator-component :operationType="task.operationType" :displayBorder="true" />
                      </v-list-item-content>
                    </v-list-item>

                    <!-- アイコン、圃場情報 -->
                    <v-list-item>
                      <user-icon-component
                        size="36"
                        :showNickname="true"
                        :iconUrl="getAccount(task).iconUrl"
                        :nickname="getAccount(task).nickname"
                        class="mr-2"
                      />
                      <v-list-item-content>
                        <v-list-item-title>
                          <strong v-if="task">{{ getFieldNameFromFieldGeoInfoKeyFromTask(task) }}</strong>
                          <strong v-else>-----</strong>
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ task.name }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <!-- 作付け -->
                    <v-list-item style="background: none">
                      <!--                    <v-list-item-subtitle v-for="p in getProducts(task)" :key="p.id">-->
                      <!--                      <product-indicator-component :product="p" :small="true" v-if="isCurrentProduct(p, task)" />-->
                      <!--                    </v-list-item-subtitle>-->
                    </v-list-item>

                    <!-- 作業情報 -->
                    <v-list-item style="background: none">
                      <v-list-item-content>
                        <v-list-item-subtitle>{{ task | aidTaskRecordEfficiencyDescription }}</v-list-item-subtitle>
                        <v-list-item-title>{{ task | aidTaskRecordDistanceDescription }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-layout>

                <v-card-actions v-if="!hidden(task)">
                  <div class="flex-grow-1"></div>
                  <v-btn color="error" @click.stop="openDeleteDialog(task)" :disabled="!isMyTask(task)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </v-card-actions>
                <v-divider></v-divider>
              </v-card>
            </v-hover>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-else justify="center" align-content="center">
      <v-col cols="8" class="pt-12">
        <v-img class="img-fluid" src="/images/tasks_list_no_work_history.jpg" />
      </v-col>
    </v-row>
    <pagination-component
      :pagination-info="paginationInfo"
      :paging-type="pagingType"
      @update-paging="updatePaging($event)"
      ref="paginationComponent"
    />
    <v-dialog v-model="isShowDeleteDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ $tt('customTaskDeleteMessage') }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="isShowDeleteDialog = false">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="deleteTaskRecordItem()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isShowBuldDeleteDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ $tt('customBulkDeleteDialogText') }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="isShowBuldDeleteDialog = false">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="bulkDeleteTask()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <now-loading-overlay-component :nowLoading="nowLoading" />
  </v-container>
</template>

<script>
import FieldNameMixin from '@/components/mixins/field-name-mixin'
import OperationTypeMixin from '@/components/mixins/operation-type-mixin'
import TaskListMixin from '@/components/mixins/task-list-mixin'
import OperationTypeIndicatorComponent from '@/components/operation/OperationTypeIndicatorComponent'
import PaginationComponent from '@/components/PaginationComponent'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import TaskFilterComponent from '@/components/tasks/TaskFilterComponent'
import UserIconComponent from '@/components/user/UserIconComponent'
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import i18n from '@/lib/i18n'
import PagingOptions from '@/lib/paging-options'
import Planting from '@/lib/planting'
import Toast from '@/lib/toast'
import UTCDateTime from '@/lib/utc-datetime'

export default {
  name: 'TaskListAsList',
  mixins: [TaskListMixin, FieldNameMixin, OperationTypeMixin],
  components: {
    PlantingIndicatorComponent,
    UserIconComponent,
    OperationTypeIndicatorComponent,
    TaskFilterComponent,
    PaginationComponent,
    NowLoadingOverlayComponent,
  },
  data() {
    return {
      pagingType: PagingOptions.TASK_PAGING_TYPE,
      isShowDeleteDialog: false,
      selectedTask: null,
      nowLoading: false,
      deleteCheckedTask: [],
      isBulkDelete: false,
      isShowBuldDeleteDialog: false,
    }
  },

  computed: {
    paginationInfo() {
      return this.$store.getters.paginationInfos.tasks
    },
  },

  mounted() {
    this.onResize()
    const params = {
      filterDateFrom: this.filterDateFrom,
      filterDateTo: this.filterDateTo,
      selectPickerType: this.selectPickerType,
    }
    this.$store.dispatch('setDateFilterRange', params)
  },

  methods: {
    onRowClick(task) {
      if (this.hidden(task)) {
        return
      }
      if (this.isBulkDelete) {
        this.toggleDeleteCheck(task)
        return
      }
      this.showDetail(task)
    },
    openDeleteDialog(task) {
      this.selectedTask = task
      this.isShowDeleteDialog = true
    },
    deleteTaskRecordItem() {
      const params = {
        task: this.selectedTask,
        onSuccess: this.onDeleteSuccess,
        onFailure: this.onDeleteFailure,
      }
      this.openNowLoading()
      this.$store.dispatch('deleteTaskRecordItem', params)
    },
    onDeleteSuccess() {
      this.$refs.taskFilterComponent.initTasksWithFilter()
      Toast.success(i18n.tt('customTaskDeleteSucceed'))
      this.isShowDeleteDialog = false
      this.closeNowLoading()
    },
    onDeleteFailure() {
      Toast.error(i18n.tt('customTaskDeleteFailure'))
      this.isShowDeleteDialog = false
      this.closeNowLoading()
    },
    openNowLoading() {
      this.nowLoading = true
    },
    closeNowLoading() {
      this.nowLoading = false
    },
    plantings(task) {
      const field = this.getFieldFromId(task.id)
      const startTime = UTCDateTime.toUtcMoment(task.startTime)
      return Planting.listForFieldAndDate(field, startTime)
    },

    updatePaging(pagingOptions) {
      this.$refs.taskFilterComponent.updatePagingOfTaskList(pagingOptions)
    },
    resetPageIndex() {
      this.$refs.paginationComponent.resetPageIndex()
    },
    isMyTask(task) {
      return this.$store.getters.loginUsername === task.username
    },
    isDeleteChecked(task) {
      return this.deleteCheckedTask.includes(task)
    },
    toggleDeleteCheck(task) {
      const position = this.deleteCheckedTask.indexOf(task)
      if (position === -1) {
        this.deleteCheckedTask.push(task)
      } else {
        this.deleteCheckedTask.splice(position, 1)
      }
    },
    bulkDeleteTask() {
      if (this.deleteCheckedTask.length === 0) {
        Toast.error(i18n.tt('customTaskDeleteFailure'))
        this.isShowBuldDeleteDialog = false
        return
      }
      this.openNowLoading()
      let workTasks = this.tasks
      this.deleteCheckedTask.forEach(task => {
        this.$store.dispatch('bulkDeleteTask', task)
        workTasks = workTasks.filter(taskItem => taskItem !== task)
      })
      this.$store.dispatch('setTasks', workTasks)
      this.closeNowLoading()
      this.isShowBuldDeleteDialog = false
    },
  },
}
</script>

<style scoped>
.footer-select-box {
  width: 90px;
}

.blur {
  -ms-filter: blur(4px);
  filter: blur(4px);
}

.clickable-row {
  cursor: pointer;
}
</style>
